import type { Address, CheckoutInput, CheckoutPaymentMethod, CheckoutResult, PortalCheckoutInput, PortalCheckoutQuote } from '@erp/shared';
import { isAxiosError } from 'axios';
import { ArrowLeft, Banknote, Check, Clock, CreditCard, Landmark, Loader2, Lock, type LucideIcon, MessageCircle, Package, ShieldCheck, Tag, Truck, User, Wallet } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { useStorefront } from '../../app/StorefrontLayout.js';
import { Button, FormField, Input } from '../../components/ui/index.js';
import { cartKey, useCartStore } from '../../lib/cartStore.js';
import { customerApi } from '../../lib/customerApi.js';
import { useCustomerAuthStore } from '../../lib/customerAuthStore.js';
import { formatMoney } from '../../lib/money.js';
import { normalizeNumericInput, safeDecimal } from '../../lib/numerals.js';
import { publicApi } from '../../lib/publicApi.js';

import { useBuyerPricing } from './buyerPricing.js';
import { useCartCurrencySync } from './cartPricing.js';
import { totalSaving, useCartSavings } from './cartSavings.js';
import { OriginalPrice } from './discountDisplay.js';
import { FibPayBox } from './FibPayBox.js';
import { GroupPricingBadge } from './GroupPricingBadge.js';
import { PhoneInput } from './PhoneInput.js';

/** Per-method icon for the payment cards. */
const METHOD_ICON: Record<CheckoutPaymentMethod, LucideIcon> = {
  fib: CreditCard,
  cod: Banknote,
  bank_transfer: Landmark,
  pay_later: Clock,
};

/** A section header — an icon chip + title (+ optional hint). Gives the form a guided, "pro" rhythm. */
function SectionHeader({ icon: Icon, title, hint }: { icon: LucideIcon; title: string; hint?: string }): JSX.Element {
  return (
    <div className="flex items-center gap-3">
      <span className="grid h-9 w-9 flex-none place-items-center rounded-xl bg-brand/10 text-brand">
        <Icon className="h-4 w-4" aria-hidden />
      </span>
      <div className="min-w-0">
        <h2 className="text-sm font-semibold text-fg">{title}</h2>
        {hint ? <p className="text-xs text-fg-muted">{hint}</p> : null}
      </div>
    </div>
  );
}

export function StorefrontCheckoutPage(): JSX.Element {
  const { t, i18n } = useTranslation('cms');
  const { slug, site } = useStorefront();
  const navigate = useNavigate();
  const cart = useCartStore();
  // The SHOPPER's basis rules this page: a signed-in customer on a price list is
  // charged in THEIR group's currency (server-side, from the party), guests in
  // the store currency. Lines carried over from another basis are re-priced first
  // so the totals, min-order gate, payload and WhatsApp message never mix.
  const buyer = useBuyerPricing();
  const { syncing } = useCartCurrencySync(buyer);
  const signedIn = useCustomerAuthStore((s) => !!s.accessToken);
  const customerPhone = useCustomerAuthStore((s) => s.customer?.phone ?? null);
  const allLines = cart.slug === slug ? cart.items : [];
  const currency = buyer.authoritative ? buyer.currencyCode : allLines[0]?.currencyCode ?? buyer.currencyCode;
  const lines = allLines.filter((l) => l.currencyCode === currency);
  const settling = allLines.length > 0 && (!buyer.resolved || syncing);
  /** This order isn't in the store currency ⇒ store-currency amounts can't join it. */
  const foreign = currency !== site.currencyCode;

  const [name, setName] = useState('');
  const [phone, setPhone] = useState(customerPhone ?? '');
  const [line1, setLine1] = useState('');
  const [line2, setLine2] = useState('');
  const [city, setCity] = useState('');
  const [region, setRegion] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [country, setCountry] = useState('');
  // FIB (pay online) leads the list + is the default when the store has it on, so
  // payment happens as part of checkout rather than as an afterthought.
  // Admin-configured enabled methods (server already dropped 'fib' unless FIB is set up).
  const paymentMethods: CheckoutPaymentMethod[] = site.paymentMethods.length ? site.paymentMethods : ['cod'];
  const [paymentMethod, setPaymentMethod] = useState<CheckoutPaymentMethod>(paymentMethods[0] ?? 'cod');
  const [couponCode, setCouponCode] = useState('');
  const [couponOpen, setCouponOpen] = useState(false);
  const [note, setNote] = useState('');
  // Cashback wallet ("use my points"). The quote is SERVER-computed (balance,
  // eligibility, the you'll-earn estimate); the client only picks how much to
  // apply — the server re-caps it authoritatively at checkout.
  const [walletQuote, setWalletQuote] = useState<PortalCheckoutQuote | null>(null);
  const [useWallet, setUseWallet] = useState(false);
  const [walletInput, setWalletInput] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  // Set to the placed order number when FIB is chosen → shows the inline pay sheet.
  const [fibOrder, setFibOrder] = useState<string | null>(null);
  const [fibPaid, setFibPaid] = useState(false);
  // Set once an order is placed so clearing the cart doesn't trigger the
  // empty-cart redirect and clobber the navigation to the confirmation page.
  const placed = useRef(false);

  // An empty cart has nothing to check out — bounce back to the shop. Waits for
  // the basis/re-price pass, otherwise a group customer whose lines are mid-flight
  // gets bounced off their own checkout.
  useEffect(() => {
    if (!settling && lines.length === 0 && !placed.current) navigate(`/s/${slug}/shop`, { replace: true });
  }, [settling, lines.length, navigate, slug]);

  const money = (v: number): string => formatMoney(String(v), { locale: i18n.language, currency });
  /** Store-currency amounts (delivery fees, the minimum order) — never the buyer's. */
  const storeMoney = (v: number): string => formatMoney(String(v), { locale: i18n.language, currency: site.currencyCode });
  const subtotal = lines.reduce((sum, l) => sum + Number(l.price) * l.quantity, 0);
  const itemCount = lines.reduce((n, l) => n + l.quantity, 0);
  // Automatic discounts are already inside the line prices (and so inside the
  // subtotal) — the summary just shows what they took off.
  const savingOf = useCartSavings(slug);
  const saved = totalSaving(lines, savingOf);
  // The minimum order is a STORE-currency scalar; the server skips it for a
  // foreign-currency group order, so the UI must not gate on it either.
  const minOrder = !foreign && site.minOrderAmount ? Number(site.minOrderAmount) : 0;
  const belowMin = minOrder > 0 && subtotal < minOrder;
  // Delivery fee for the selected city (admin-configured). The server re-charges
  // it authoritatively; this is the shopper-facing preview + grand total. The fee
  // is a STORE-currency amount, so it can't ride on a foreign-currency order —
  // the server omits it there (and flags it on the order for staff).
  const deliveryCities = site.deliveryCities ?? [];
  const selectedCity = deliveryCities.find((c) => c.name.toLowerCase() === city.trim().toLowerCase());
  const cityFee = selectedCity ? Number(selectedCity.price) || 0 : 0;
  const deliveryFee = foreign ? 0 : cityFee;
  const total = subtotal + deliveryFee;

  // Fetch the cashback quote once the cart has settled (signed-in customers on a
  // loyalty-enabled store only). An older API without the endpoint just hides
  // the widget (catch → null).
  const linesKey = lines.map((l) => `${l.itemId}:${l.itemUnitId ?? ''}:${l.itemVariantId ?? ''}:${l.quantity}`).join('|');
  useEffect(() => {
    if (!signedIn || !site.loyalty || settling || lines.length === 0) { setWalletQuote(null); return; }
    let stale = false;
    const payload = { lines: lines.map((l) => ({ itemId: l.itemId, ...(l.itemUnitId ? { itemUnitId: l.itemUnitId } : {}), ...(l.itemVariantId ? { itemVariantId: l.itemVariantId } : {}), quantity: l.quantity })) };
    void customerApi().post<PortalCheckoutQuote>('/checkout/quote', payload)
      .then((r) => { if (!stale) setWalletQuote(r.data); })
      .catch(() => { if (!stale) setWalletQuote(null); });
    return () => { stale = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- linesKey captures the cart content
  }, [signedIn, site.loyalty, settling, linesKey]);

  // Wallet spend is only offered when the server says this cart is eligible AND
  // the order currency IS the wallet currency (base) AND there is a balance.
  const walletBalance = walletQuote ? Number(walletQuote.balance) : 0;
  const walletOffered = signedIn && !!walletQuote && walletQuote.eligible && currency === walletQuote.currencyCode && walletBalance > 0;
  const walletMax = walletQuote ? Math.min(walletBalance, total * (walletQuote.maxRedeemPct / 100)) : 0;
  const walletApplied = walletOffered && useWallet
    ? Math.max(0, Math.min(safeDecimal(walletInput).toNumber(), walletMax))
    : 0;
  const payable = Math.max(0, total - walletApplied);
  /** The policy — not the balance — is what's holding the amount back. Say so. */
  const walletCapped = !!walletQuote && walletQuote.maxRedeemPct < 100 && walletMax < walletBalance;

  // The ceiling MOVES: editing the cart or picking a different delivery city
  // changes the total, and with it `total × maxRedeemPct`. An amount typed when
  // the cart was bigger has to come back down with it, or the field would keep
  // showing a number the order will not actually use.
  useEffect(() => {
    if (!useWallet) return;
    const typed = safeDecimal(walletInput).toNumber();
    if (Number.isFinite(typed) && typed > walletMax) setWalletInput(String(walletMax));
  }, [useWallet, walletInput, walletMax]);
  const willEarn = walletQuote && Number(walletQuote.willEarn) > 0 ? Number(walletQuote.willEarn) : 0;
  const walletMoney = (v: number): string => formatMoney(String(v), { locale: i18n.language, currency: walletQuote?.currencyCode ?? currency });

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    // A signed-in customer's order is bound to their party server-side, so their
    // name isn't needed (the phone still is — it opens the order page after).
    if (!signedIn && !name.trim()) e.name = t('checkout.required');
    if (phone.replace(/\D/g, '').length < 9) e.phone = t('checkout.badPhone', { defaultValue: 'Enter a valid phone number.' });
    // Address is optional, but if any address field is started it must be complete.
    const anyAddr = [line1, line2, city, region, postalCode, country].some((v) => v.trim());
    if (anyAddr) {
      if (!line1.trim()) e.line1 = t('checkout.required');
      if (!city.trim()) e.city = t('checkout.required');
      if (!/^[A-Za-z]{2}$/.test(country.trim())) e.country = t('checkout.badCountry');
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (): Promise<void> => {
    if (submitting || belowMin || settling || !validate()) return;
    setSubmitting(true);
    const hasAddr = line1.trim() && city.trim() && /^[A-Za-z]{2}$/.test(country.trim());
    const shippingAddress: Address | undefined = hasAddr
      ? {
          line1: line1.trim(),
          ...(line2.trim() ? { line2: line2.trim() } : {}),
          city: city.trim(),
          ...(region.trim() ? { region: region.trim() } : {}),
          ...(postalCode.trim() ? { postalCode: postalCode.trim() } : {}),
          country: country.trim().toUpperCase(),
        }
      : undefined;
    const order = {
      lines: lines.map((l) => ({ itemId: l.itemId, ...(l.itemUnitId ? { itemUnitId: l.itemUnitId } : {}), ...(l.itemVariantId ? { itemVariantId: l.itemVariantId } : {}), quantity: l.quantity })),
      ...(shippingAddress ? { shippingAddress } : {}),
      ...(city.trim() ? { deliveryCity: city.trim() } : {}),
      paymentMethod,
      ...(couponCode.trim() ? { couponCode: couponCode.trim() } : {}),
      ...(note.trim() ? { note: note.trim() } : {}),
    };
    try {
      // Signed in ⇒ place the order through the PORTAL endpoint, which binds it to
      // the customer's own party server-side. That's what makes the charge use the
      // very group prices/currency this page just showed (a guest checkout would
      // only match by phone, and an unmatched phone would silently re-price the
      // order into the store currency).
      const { data } = signedIn
        ? await customerApi().post<CheckoutResult>('/checkout', {
            ...order,
            // Spend the wallet: the server re-validates against the REAL balance
            // and caps at the order total — this is only the customer's ask.
            ...(walletApplied > 0 ? { cashbackAmount: walletApplied.toFixed(4) } : {}),
          } satisfies PortalCheckoutInput)
        : await publicApi().post<CheckoutResult>(`/${slug}/checkout`, { ...order, contact: { name: name.trim(), phone: phone.trim() } } satisfies CheckoutInput);
      // The order page looks an order up by its PARTY's phone: for a portal order
      // that's the account's own number, not whatever was typed in the form.
      const placedPhone = (signedIn ? customerPhone?.trim() : '') || phone.trim();
      placed.current = true;
      cart.clear();
      if (paymentMethod === 'fib') {
        // Pay online right here — the FIB QR opens immediately, so the order is
        // paid as the final step instead of on a page the shopper can bounce off.
        setFibOrder(data.orderNumber);
      } else {
        navigate(`/s/${slug}/order/${encodeURIComponent(data.orderNumber)}`, { state: { phone: placedPhone } });
      }
    } catch (err) {
      const cause = isAxiosError(err) ? (err.response?.data as { cause?: { code?: string } } | undefined)?.cause?.code : undefined;
      if (cause === 'below_min_order') {
        toast.error(t('checkout.belowMin', { defaultValue: 'Your order is below the {{amount}} minimum.', amount: storeMoney(minOrder) }));
      } else if (cause === 'group_price_unavailable' || cause === 'price_currency_unavailable') {
        // The server refused to charge a price it couldn't denominate in the
        // buyer's currency — never converted behind their back.
        toast.error(t('checkout.groupPriceUnavailable', { defaultValue: "Some items aren't priced for your account. Please review your cart." }));
      } else if (signedIn && isAxiosError(err) && err.response?.status === 401) {
        toast.error(t('checkout.sessionExpired', { defaultValue: 'Your session expired. Please sign in again.' }));
      } else if (isAxiosError(err) && err.response?.status === 422) {
        toast.error(t('checkout.itemUnavailable'));
      } else if (isAxiosError(err) && err.response?.status === 429) {
        toast.error(t('checkout.tooMany'));
      } else {
        toast.error(t('checkout.failed'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Click-to-send WhatsApp order: opens wa.me pre-filled with the cart + whatever
  // contact/address the customer has entered (no order is placed server-side).
  const orderViaWhatsApp = (): void => {
    if (!site.whatsappNumber) return;
    const addressText = [line1, line2, city, region, country].map((v) => v.trim()).filter(Boolean).join(', ');
    const items = lines.map((l) => `• ${l.name} × ${l.quantity} = ${money(Number(l.price) * l.quantity)}`).join('\n');
    const body = [
      `🛒 ${t('checkout.wa.title', { defaultValue: 'New order — {{store}}', store: site.storeName })}`,
      '',
      name.trim() && `👤 ${name.trim()}`,
      phone.trim() && `📞 ${phone.trim()}`,
      addressText && `📍 ${addressText}`,
      '',
      `${t('checkout.wa.items', { defaultValue: 'Items' })}:`,
      items,
      '',
      `${t('cart.subtotal')}: ${money(subtotal)}`,
      deliveryFee > 0 && `${t('checkout.delivery', { defaultValue: 'Delivery' })}: ${money(deliveryFee)}`,
      // A store-currency delivery fee can't be added to a foreign-currency order.
      foreign && cityFee > 0 && `${t('checkout.delivery', { defaultValue: 'Delivery' })}: ${storeMoney(cityFee)} (${t('checkout.deliverySeparate', { defaultValue: 'charged separately' })})`,
      `${t('cart.total', { defaultValue: 'Total' })}: ${money(total)}`,
      `${t('checkout.payment')}: ${t(`checkout.method.${paymentMethod}`)}`,
      couponCode.trim() && `${t('checkout.coupon')}: ${couponCode.trim()}`,
      note.trim() && `📝 ${note.trim()}`,
    ].filter(Boolean).join('\n');
    window.open(`https://wa.me/${site.whatsappNumber}?text=${encodeURIComponent(body)}`, '_blank', 'noopener,noreferrer');
  };

  // Inline FIB pay step — shown once a FIB order is placed (the cart is now empty,
  // so this replaces the form). Pay here, then head to the order.
  if (fibOrder) {
    const goToOrder = (): void =>
      navigate(`/s/${slug}/order/${encodeURIComponent(fibOrder)}`, { state: { phone: phone.trim() } });
    return (
      <div className="mx-auto flex w-full max-w-md flex-col gap-6 py-8">
        <Helmet><title>{`${t('checkout.title')} · ${site.storeName}`}</title></Helmet>
        <div>
          <h1 className="text-2xl font-semibold">{t('checkout.fibTitle', { defaultValue: 'Pay for order {{n}}', n: fibOrder })}</h1>
          <p className="mt-1 text-sm text-fg-muted">{t('checkout.fibHint', { defaultValue: 'Complete the payment to confirm your order.' })}</p>
        </div>
        <div className="flex flex-col items-center gap-4">
          <FibPayBox slug={slug} orderNumber={fibOrder} autoStart onPaid={() => setFibPaid(true)} />
        </div>
        {fibPaid ? (
          <Button onClick={goToOrder} size="lg" variant="accent" className="w-full">{t('checkout.viewOrder', { defaultValue: 'View my order' })}</Button>
        ) : (
          <button type="button" onClick={goToOrder} className="text-sm text-fg-muted underline-offset-4 hover:text-fg hover:underline">
            {t('checkout.payLater', { defaultValue: "I'll pay later — view my order" })}
          </button>
        )}
      </div>
    );
  }

  // The cart is being re-priced into the shopper's currency — never render (or
  // let them submit) totals that are about to change denomination.
  if (settling) {
    return (
      <div className="mx-auto flex w-full max-w-md flex-col items-center gap-3 py-24 text-center text-fg-muted">
        <Loader2 className="h-6 w-6 animate-spin text-brand" aria-hidden />
        <p className="text-sm">{t('cart.repricing', { defaultValue: 'Updating prices for your account…' })}</p>
      </div>
    );
  }

  if (lines.length === 0) return <div className="py-16" />;

  return (
    <div className="sf-anim mx-auto flex w-full max-w-5xl flex-col gap-6">
      <Helmet><title>{`${t('checkout.title')} · ${site.storeName}`}</title></Helmet>
      <div className="flex flex-col gap-3">
        <Link to={`/s/${slug}/cart`} className="inline-flex w-fit items-center gap-1 text-sm text-fg-muted transition hover:text-fg">
          <ArrowLeft className="h-4 w-4 rtl:-scale-x-100" aria-hidden />{t('checkout.backToCart')}
        </Link>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-2xl font-bold tracking-tight">{t('checkout.title')}</h1>
          <div className="flex flex-wrap items-center gap-2">
            <GroupPricingBadge />
            <span className="inline-flex items-center gap-1.5 rounded-full bg-success/10 px-2.5 py-1 text-xs font-medium text-success">
              <ShieldCheck className="h-3.5 w-3.5" aria-hidden />{t('checkout.secure', { defaultValue: 'Secure checkout' })}
            </span>
          </div>
        </div>
      </div>

      <form
        className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_22rem]"
        onSubmit={(e) => { e.preventDefault(); void submit(); }}
      >
        <div className="flex flex-col gap-5">
          <section className="sf-card flex flex-col gap-4 rounded-2xl border border-border bg-bg p-5 shadow-sm" style={{ '--sf-delay': '0ms' } as React.CSSProperties}>
            <SectionHeader icon={User} title={t('checkout.contact')} />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField id="co-name" label={t('checkout.name')} required={!signedIn} error={errors.name}>
                <Input id="co-name" value={name} onChange={(e) => setName(e.target.value)} invalid={!!errors.name} autoComplete="name" />
              </FormField>
              <FormField id="co-phone" label={t('checkout.phone')} required error={errors.phone}>
                {/* The shared picker, like every other phone field in the product.
                    This one was still a bare tel input asking the customer to type
                    "+9647…" from a placeholder — and it is the phone field that
                    matters most, because it is the only handle the shop has on a
                    guest order and the key the order-lookup page matches on. The
                    picker defaults to Iraq and drops a leading zero, so the number
                    that reaches the server is the same shape wherever it was typed. */}
                <PhoneInput id="co-phone" value={phone} onChange={setPhone} />
              </FormField>
            </div>
          </section>

          <section className="sf-card flex flex-col gap-4 rounded-2xl border border-border bg-bg p-5 shadow-sm" style={{ '--sf-delay': '60ms' } as React.CSSProperties}>
            <SectionHeader icon={Truck} title={t('checkout.shipping')} hint={t('checkout.shippingOptional')} />
            <FormField id="co-line1" label={t('checkout.line1')} error={errors.line1}>
              <Input id="co-line1" value={line1} onChange={(e) => setLine1(e.target.value)} invalid={!!errors.line1} autoComplete="address-line1" />
            </FormField>
            <FormField id="co-line2" label={t('checkout.line2')}>
              <Input id="co-line2" value={line2} onChange={(e) => setLine2(e.target.value)} autoComplete="address-line2" />
            </FormField>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField id="co-city" label={t('checkout.city')} error={errors.city}>
                {deliveryCities.length > 0 ? (
                  <select
                    id="co-city"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className={`block h-10 w-full rounded-md border bg-bg px-3 text-sm text-fg outline-none transition focus:border-brand focus:ring-2 focus:ring-brand/20 ${errors.city ? 'border-danger' : 'border-border'}`}
                  >
                    <option value="">{t('checkout.citySelect', { defaultValue: 'Select your city' })}</option>
                    {deliveryCities.map((c) => (
                      <option key={c.name} value={c.name}>
                        {/* City fees are STORE-currency amounts — label them as such. */}
                        {Number(c.price) > 0 ? `${c.name} — ${storeMoney(Number(c.price))}` : c.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <Input id="co-city" value={city} onChange={(e) => setCity(e.target.value)} invalid={!!errors.city} autoComplete="address-level2" />
                )}
              </FormField>
              <FormField id="co-region" label={t('checkout.region')}>
                <Input id="co-region" value={region} onChange={(e) => setRegion(e.target.value)} autoComplete="address-level1" />
              </FormField>
              <FormField id="co-postal" label={t('checkout.postalCode')}>
                <Input id="co-postal" value={postalCode} onChange={(e) => setPostalCode(e.target.value)} autoComplete="postal-code" />
              </FormField>
              <FormField id="co-country" label={t('checkout.country')} helperText={t('checkout.countryHint')} error={errors.country}>
                <Input id="co-country" value={country} maxLength={2} onChange={(e) => setCountry(e.target.value.toUpperCase())} invalid={!!errors.country} autoComplete="country" />
              </FormField>
            </div>
          </section>

          <section className="sf-card flex flex-col gap-4 rounded-2xl border border-border bg-bg p-5 shadow-sm" style={{ '--sf-delay': '120ms' } as React.CSSProperties}>
            <SectionHeader icon={CreditCard} title={t('checkout.payment')} />
            <div className="flex flex-col gap-2.5">
              {paymentMethods.map((m) => {
                const Icon = METHOD_ICON[m];
                const active = paymentMethod === m;
                return (
                  <label
                    key={m}
                    className={`group flex cursor-pointer items-center gap-3 rounded-xl border p-3.5 transition-[border-color,background-color,box-shadow,transform] duration-150 ease-out focus-within:ring-2 focus-within:ring-brand/40 active:scale-[0.99] ${active ? 'border-brand bg-brand/5 ring-1 ring-brand/40' : 'border-border hover:border-fg-subtle'}`}
                  >
                    <input type="radio" name="payment" value={m} checked={active} onChange={() => setPaymentMethod(m)} className="sr-only" />
                    <span className={`grid h-9 w-9 flex-none place-items-center rounded-lg transition-colors ${active ? 'bg-brand text-white' : 'bg-bg-subtle text-fg-muted'}`}>
                      <Icon className="h-4 w-4" aria-hidden />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-fg">{t(`checkout.method.${m}`)}</span>
                      <span className="block text-xs text-fg-muted">{t(`checkout.methodHint.${m}`)}</span>
                    </span>
                    <span className={`grid h-5 w-5 flex-none place-items-center rounded-full border-2 transition ${active ? 'border-brand bg-brand' : 'border-border'}`}>
                      {active ? <Check className="h-3 w-3 text-white" strokeWidth={3} aria-hidden /> : null}
                    </span>
                  </label>
                );
              })}
            </div>
          </section>

          <section className="sf-card flex flex-col gap-4 rounded-2xl border border-border bg-bg p-5 shadow-sm" style={{ '--sf-delay': '180ms' } as React.CSSProperties}>
            {couponOpen ? (
              <FormField id="co-coupon" label={t('checkout.coupon')} helperText={t('checkout.couponHint')}>
                <Input id="co-coupon" value={couponCode} onChange={(e) => setCouponCode(e.target.value.toUpperCase())} autoFocus />
              </FormField>
            ) : (
              <button type="button" onClick={() => setCouponOpen(true)} className="inline-flex w-fit items-center gap-2 text-sm font-medium text-brand transition hover:opacity-80">
                <Tag className="h-4 w-4" aria-hidden />{t('checkout.haveCoupon', { defaultValue: 'Have a promo code?' })}
              </button>
            )}
            <FormField id="co-note" label={t('checkout.note')}>
              <textarea
                id="co-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                maxLength={1000}
                className="block w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg outline-none transition focus:border-brand focus:ring-2 focus:ring-brand/20"
              />
            </FormField>
          </section>
        </div>

        <aside className="sf-card flex h-fit flex-col gap-4 rounded-2xl border border-border bg-bg-subtle p-5 shadow-sm lg:sticky lg:top-24" style={{ '--sf-delay': '90ms' } as React.CSSProperties}>
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-fg">{t('checkout.summary')}</h2>
            <span className="rounded-full bg-bg px-2.5 py-0.5 text-xs font-medium text-fg-muted">
              {t('checkout.itemCount', { count: itemCount, defaultValue: `${itemCount} items` })}
            </span>
          </div>
          <ul className="flex flex-col gap-3">
            {lines.map((l) => {
              const saving = savingOf(l);
              return (
                <li key={cartKey(l)} className="flex items-center gap-3">
                  <div className="relative h-14 w-14 flex-none overflow-hidden rounded-lg border border-border bg-white">
                    {l.imageUrl ? (
                      <img src={l.imageUrl} alt="" loading="lazy" className="h-full w-full object-contain p-1" />
                    ) : (
                      <span className="grid h-full w-full place-items-center text-fg-subtle"><Package className="h-5 w-5" aria-hidden /></span>
                    )}
                    <span className="absolute -end-1.5 -top-1.5 grid h-5 min-w-[1.25rem] place-items-center rounded-full bg-brand px-1 text-[0.65rem] font-bold text-white shadow-sm">{l.quantity}</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-fg">{l.name}</span>
                    <span className="flex flex-wrap items-baseline gap-x-1.5 text-xs text-fg-muted">
                      <span>{money(Number(l.price))} × {l.quantity}</span>
                      {saving && <OriginalPrice amount={saving.originalPrice} currency={currency} />}
                    </span>
                  </div>
                  <span className="flex-none font-mono text-sm tabular-nums text-fg">{money(Number(l.price) * l.quantity)}</span>
                </li>
              );
            })}
          </ul>
          <div className="flex flex-col gap-2 border-t border-border pt-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-fg-muted">{t('cart.subtotal')}</span>
              <span className="font-mono tabular-nums">{money(subtotal)}</span>
            </div>
            {saved > 0 && (
              // Already inside the subtotal above — past tense, and never added
              // to (or subtracted from) the total again.
              <div className="flex items-center justify-between text-sm font-medium text-success">
                <span>{t('cart.saved')}</span>
                <span className="font-mono tabular-nums">{money(saved)}</span>
              </div>
            )}
            <div className="flex items-center justify-between text-sm">
              <span className="text-fg-muted">{t('checkout.delivery', { defaultValue: 'Delivery' })}{selectedCity ? ` · ${selectedCity.name}` : ''}</span>
              <span className="font-mono tabular-nums">
                {deliveryFee > 0
                  ? money(deliveryFee)
                  : foreign && cityFee > 0
                    ? <span className="font-sans text-xs font-medium text-fg-muted">{storeMoney(cityFee)}</span>
                    : <span className="font-sans font-semibold text-success">{t('checkout.deliveryFree', { defaultValue: 'Free' })}</span>}
              </span>
            </div>
            {foreign && cityFee > 0 && (
              // The fee is a store-currency amount and the order is in the group's
              // currency — the server leaves it off and flags it for staff.
              <p className="text-xs text-fg-muted">
                {t('checkout.deliveryNotInOrder', {
                  defaultValue: 'The {{amount}} delivery fee is settled separately — it is not added to this {{currency}} total.',
                  amount: storeMoney(cityFee),
                  currency,
                })}
              </p>
            )}
          </div>
          {walletOffered && (
            <div className="flex flex-col gap-2 rounded-xl border border-brand/30 bg-brand/5 p-3">
              <label className="flex cursor-pointer items-center gap-2.5">
                <input
                  type="checkbox"
                  checked={useWallet}
                  onChange={(e) => {
                    setUseWallet(e.target.checked);
                    if (e.target.checked && !walletInput.trim()) setWalletInput(String(Math.min(walletBalance, walletMax)));
                  }}
                  className="h-4 w-4 accent-brand"
                />
                <span className="flex min-w-0 flex-1 items-center gap-1.5 text-sm font-medium text-fg">
                  <Wallet className="h-4 w-4 flex-none text-brand" aria-hidden />
                  {t('checkout.wallet.use', { defaultValue: 'Use my balance' })}
                </span>
                <span className="font-mono text-sm font-semibold tabular-nums text-brand">{walletMoney(walletBalance)}</span>
              </label>
              {useWallet && (
                <div className="flex items-center gap-2">
                  <Input
                    id="co-wallet"
                    inputMode="decimal"
                    value={walletInput}
                    onChange={(e) => {
                      // Refuse the over-cap value outright rather than accepting it
                      // and quietly spending less than the field says.
                      const v = normalizeNumericInput(e.target.value);
                      const n = safeDecimal(v).toNumber();
                      setWalletInput(Number.isFinite(n) && n > walletMax ? String(walletMax) : v);
                    }}
                    aria-label={t('checkout.wallet.amount', { defaultValue: 'Amount to use' })}
                    className="h-9"
                  />
                  <button
                    type="button"
                    onClick={() => setWalletInput(String(Math.min(walletBalance, walletMax)))}
                    className="flex-none text-xs font-medium text-brand transition hover:opacity-80"
                  >
                    {t('checkout.wallet.max', { defaultValue: 'Max' })}
                  </button>
                </div>
              )}
              {useWallet && walletCapped && (
                <p className="text-xs text-fg-muted">
                  {t('checkout.wallet.capped', {
                    defaultValue: 'You can pay at most {{pct}}% of this order with your balance — {{amount}}.',
                    pct: walletQuote?.maxRedeemPct ?? 100,
                    amount: walletMoney(walletMax),
                  })}
                </p>
              )}
              {walletApplied > 0 && (
                <div className="flex items-center justify-between text-sm text-success">
                  <span>{t('checkout.wallet.applied', { defaultValue: 'Balance used' })}</span>
                  <span className="font-mono tabular-nums">−{walletMoney(walletApplied)}</span>
                </div>
              )}
            </div>
          )}
          <div className="flex items-end justify-between border-t border-border pt-3">
            <span className="text-sm font-semibold">{t('cart.total', { defaultValue: 'Total' })}</span>
            <span className="font-mono text-xl font-bold tabular-nums text-fg">{money(payable)}</span>
          </div>
          {willEarn > 0 && (
            <p className="flex items-center gap-1.5 text-xs font-medium text-brand">
              <Wallet className="h-3.5 w-3.5 flex-none" aria-hidden />
              {t('checkout.wallet.willEarn', { defaultValue: "You'll earn {{amount}} cashback when this order is fulfilled.", amount: walletMoney(willEarn) })}
            </p>
          )}
          {belowMin ? (
            <div className="flex flex-col gap-1.5">
              <p className="text-xs font-medium text-danger">
                {t('checkout.minNotice', { defaultValue: 'Minimum order is {{amount}}. Add {{remaining}} more.', amount: storeMoney(minOrder), remaining: storeMoney(minOrder - subtotal) })}
              </p>
              <div className="h-1.5 overflow-hidden rounded-full bg-danger/15">
                <div className="h-full rounded-full bg-danger transition-[width] duration-300 ease-out" style={{ width: `${Math.min(100, (subtotal / minOrder) * 100)}%` }} />
              </div>
            </div>
          ) : (
            <p className="text-[0.7rem] leading-relaxed text-fg-subtle">{t('cart.estimateNote')}</p>
          )}
          <Button type="submit" size="lg" variant="accent" loading={submitting} disabled={belowMin} className="w-full">{t('checkout.placeOrder')}</Button>
          <p className="flex items-center justify-center gap-1.5 text-xs text-fg-muted">
            <Lock className="h-3 w-3" aria-hidden />{t('checkout.secureNote', { defaultValue: 'Your details stay private.' })}
          </p>

          {site.whatsappNumber && (
            <>
              <div className="flex items-center gap-2 text-xs text-fg-subtle">
                <span className="h-px flex-1 bg-border" />{t('checkout.wa.or', { defaultValue: 'or' })}<span className="h-px flex-1 bg-border" />
              </div>
              <button
                type="button"
                onClick={orderViaWhatsApp}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#25D366] px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:opacity-90 active:scale-[0.98]"
              >
                <MessageCircle className="h-4 w-4" aria-hidden />{t('checkout.wa.button', { defaultValue: 'Order via WhatsApp' })}
              </button>
            </>
          )}
        </aside>
      </form>
    </div>
  );
}
